import { createContext, useContext } from "react";

/**
 * A host-supplied realtime subscription source for a plugin.
 *
 * The SDK keeps this intentionally dumb — it only calls `source.subscribe`.
 * All SignalR wiring, extensionId filtering, and connection lifecycle management
 * live host-side (Task 6 in coreconnect-web). This lets the SDK be tested with
 * a simple fake source.
 */
export interface PluginRealtimeSource
{
    /**
     * Subscribe to notifications whose `typeCode` matches the given value.
     *
     * @param typeCode  The application-level event type code to filter on
     *                  (e.g. `"HelpdeskTicketCreated"`). Filtering by
     *                  extensionId is the host's responsibility.
     * @param handler   Called with the raw notification payload whenever a
     *                  matching notification arrives.
     * @returns         An unsubscribe function. Calling it removes this handler.
     */
    subscribe(typeCode: string, handler: (payload: unknown) => void): () => void;
}

/**
 * React context carrying the plugin's active {@link PluginRealtimeSource}.
 *
 * `null` is the explicit "not provided" sentinel — hooks must treat null as a
 * clean no-op (dev/mock/no-connection) rather than an error.
 *
 * Provided by {@link ExtensionRuntimeProvider} when the host passes a
 * `realtime` prop; consumed by `usePluginRealtimeSource()`.
 */
export const PluginRealtimeContext = createContext<PluginRealtimeSource | null>(null);

/**
 * Returns the {@link PluginRealtimeSource} from context, or `null` when none
 * is wired (dev/mock environments, unit tests that only care about MCP).
 *
 * Hooks built on top of this (e.g. `usePluginRealtime` in `plugin-ui`) should
 * skip their subscription entirely when this returns `null`.
 */
export function usePluginRealtimeSource(): PluginRealtimeSource | null
{
    return useContext(PluginRealtimeContext);
}
